/**
 * Per-column editability detection for query editor results.
 *
 * Determines which columns can be edited inline based on whether
 * their source table's full PK is present in the result set.
 */

import type { QueryColumn } from '../../../../types/queryExecution';

/**
 * Source table info for an editable column (used when building UPDATE statements).
 */
export type EditableColumnInfo = {
  schema: string;
  table: string;
  sourceColumn: string;
};

/**
 * Info for a source table that is editable (all PKs present in result set).
 */
export type EditableTableInfo = {
  schema: string;
  table: string;
  pkColumns: string[];
};

/**
 * Result of editability detection for a set of query columns.
 */
export type ColumnEditability = {
  /** Which columns are editable (column key: id/name → source table info) */
  editableColumns: Map<string, EditableColumnInfo>;
  /** Which columns are read-only (column key: id/name → reason) */
  readOnlyColumns: Map<string, string>;
  /** Distinct source tables that have all PKs present (for UPDATE targeting) */
  editableTables: Map<string, EditableTableInfo>;
  /** Whether any column is editable at all */
  hasEditableColumns: boolean;
  /** Human-readable reason why editing is unavailable (only set when hasEditableColumns is false) */
  readOnlyReason?: string;
};

/**
 * Analyze query columns and determine per-column editability.
 *
 * A column is editable when:
 * 1. It has source table provenance (sourceSchema + sourceTable + sourceColumn)
 * 2. The source table primary key is known and ALL PK columns are present in the result set
 * 3. The column itself is NOT a PK column (can't edit row identity)
 */
export function detectColumnEditability(columns: QueryColumn[]): ColumnEditability {
  const editableColumns = new Map<string, EditableColumnInfo>();
  const readOnlyColumns = new Map<string, string>();
  const editableTables = new Map<string, EditableTableInfo>();

  // Step 1: Group columns by source table
  type TableGroup = {
    schema: string;
    table: string;
    columns: Array<{ key: string; sourceColumn: string; isPrimaryKey: boolean }>;
    sourceColumnsInResult: Set<string>;
    primaryKeyColumnsForTable?: string[];
  };

  const tableGroups = new Map<string, TableGroup>();

  for (const col of columns) {
    const key = col.id ?? col.name;

    if (col.isComputed === true) {
      readOnlyColumns.set(key, 'Computed column');
      continue;
    }

    if (!col.sourceTable || !col.sourceSchema || !col.sourceColumn) {
      // No source table — computed/expression or SQLite
      readOnlyColumns.set(key, 'Source table unknown');
      continue;
    }

    const tableKey = `${col.sourceSchema}.${col.sourceTable}`;
    let group = tableGroups.get(tableKey);
    if (!group) {
      group = {
        schema: col.sourceSchema,
        table: col.sourceTable,
        columns: [],
        sourceColumnsInResult: new Set<string>(),
      };
      tableGroups.set(tableKey, group);
    }

    if (group.primaryKeyColumnsForTable === undefined && col.sourceTablePrimaryKeyColumns !== undefined) {
      group.primaryKeyColumnsForTable = col.sourceTablePrimaryKeyColumns;
    }

    group.columns.push({
      key,
      sourceColumn: col.sourceColumn,
      isPrimaryKey: col.isPrimaryKey === true,
    });

    group.sourceColumnsInResult.add(col.sourceColumn);
  }

  // Step 2: Determine editability per table group
  for (const [tableKey, group] of tableGroups) {
    const primaryKeyColumnsForTable = group.primaryKeyColumnsForTable;

    if (primaryKeyColumnsForTable === undefined) {
      // Can't safely update without knowing the full PK
      for (const col of group.columns) {
        readOnlyColumns.set(col.key, 'Primary key metadata unavailable');
      }
      continue;
    }

    if (primaryKeyColumnsForTable.length === 0) {
      // Table has no primary key → all columns are read-only
      for (const col of group.columns) {
        readOnlyColumns.set(col.key, 'Table has no primary key');
      }
      continue;
    }

    const missingPkColumns = primaryKeyColumnsForTable.filter(
      (pkCol) => !group.sourceColumnsInResult.has(pkCol)
    );

    if (missingPkColumns.length > 0) {
      // Incomplete PK in result → all columns from this table are read-only
      const label = missingPkColumns.length === 1 ? 'Missing primary key column' : 'Missing primary key columns';
      const reason = `${label}: ${missingPkColumns.join(', ')}`;
      for (const col of group.columns) {
        readOnlyColumns.set(col.key, reason);
      }
      continue;
    }

    // Table is editable — register it
    editableTables.set(tableKey, {
      schema: group.schema,
      table: group.table,
      pkColumns: primaryKeyColumnsForTable,
    });

    // Mark columns as editable or read-only
    for (const col of group.columns) {
      if (col.isPrimaryKey) {
        readOnlyColumns.set(col.key, 'Primary key column');
      } else {
        editableColumns.set(col.key, {
          schema: group.schema,
          table: group.table,
          sourceColumn: col.sourceColumn,
        });
      }
    }
  }

  const hasEditableColumns = editableColumns.size > 0;

  // Build a summary reason when nothing is editable
  let readOnlyReason: string | undefined;
  if (!hasEditableColumns && columns.length > 0) {
    readOnlyReason = summarizeReadOnlyReason(readOnlyColumns);
  }

  return {
    editableColumns,
    readOnlyColumns,
    editableTables,
    hasEditableColumns,
    readOnlyReason,
  };
}

/**
 * Pick the most common read-only reason across all columns
 * and return a user-friendly summary string.
 */
function summarizeReadOnlyReason(readOnlyColumns: Map<string, string>): string {
  if (readOnlyColumns.size === 0) return 'No columns in result set';

  // Count occurrences of each reason
  const counts = new Map<string, number>();
  for (const reason of readOnlyColumns.values()) {
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }

  // Pick the most frequent reason
  let topReason = '';
  let topCount = 0;
  for (const [reason, count] of counts) {
    if (count > topCount) {
      topReason = reason;
      topCount = count;
    }
  }

  return topReason;
}
